import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { format, isToday, isYesterday } from 'date-fns';
import Header from '../../components/ui/Header';

import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { messageService, userProfileService } from '../../services/supabaseService';
import ContactList from './components/ContactList';
import ConversationArea from './components/ConversationArea';
import MessageInput from './components/MessageInput';

const CommunicationHub = () => {
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const isTrainer = userProfile?.role === 'personal_trainer';

  useEffect(() => {
    loadContacts();
  }, [user, userProfile]);

  useEffect(() => {
    if (selectedContact) {
      loadMessages();
      
      // Subscribe to new messages
      const unsubscribeMessages = messageService?.subscribeToMessages(user?.id, (newMsg) => {
        if (newMsg?.senderId === selectedContact?.id || newMsg?.recipientId === selectedContact?.id) {
          setMessages(prev => {
            const exists = prev?.some(m => m?.id === newMsg?.id);
            if (exists) return prev;
            return [...prev, newMsg];
          });
          
          // Mark as read if message is from selected contact
          if (newMsg?.senderId === selectedContact?.id) {
            messageService?.markAsRead(newMsg?.id);
          }
        }
      });

      // Subscribe to message status updates (read receipts)
      const unsubscribeUpdates = messageService?.subscribeToMessageUpdates(user?.id, (updatedMsg) => {
        if (updatedMsg?.senderId === selectedContact?.id || updatedMsg?.recipientId === selectedContact?.id) {
          setMessages(prev => 
            prev?.map(m => m?.id === updatedMsg?.id ? updatedMsg : m)
          );
        }
      });

      return () => {
        unsubscribeMessages?.();
        unsubscribeUpdates?.();
      };
    }
  }, [selectedContact, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    filterContacts();
  }, [searchQuery, contacts]);

  const loadContacts = async () => {
    setIsLoading(true);
    try {
      if (isTrainer) {
        const { data } = await userProfileService?.getTrainerClients(user?.id);
        const clientsList = data?.map(a => a?.client) || [];
        setContacts(clientsList);
        setFilteredContacts(clientsList);
        if (clientsList?.length > 0) {
          setSelectedContact(clientsList?.[0]);
        }
      } else {
        const { data } = await userProfileService?.getClientTrainer(user?.id);
        if (data?.trainer) {
          setContacts([data?.trainer]);
          setFilteredContacts([data?.trainer]);
          setSelectedContact(data?.trainer);
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedContact) return;
    try {
      const { data } = await messageService?.getConversation(user?.id, selectedContact?.id);
      setMessages(data || []);
      
      // Mark unread messages as read
      const unreadMessages = data?.filter(m => 
        m?.recipientId === user?.id && m?.status !== 'read'
      );
      unreadMessages?.forEach(msg => {
        messageService?.markAsRead(msg?.id);
      });
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedContact || isSending) return;

    setIsSending(true);
    const { data, error } = await messageService?.sendMessage(selectedContact?.id, newMessage);
    if (!error && data) {
      setMessages(prev => [...prev, data]);
      setNewMessage('');
    }
    setIsSending(false);
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filterContacts = () => {
    if (!searchQuery?.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    const query = searchQuery?.toLowerCase();
    const filtered = contacts?.filter(contact => 
      contact?.fullName?.toLowerCase()?.includes(query) ||
      contact?.email?.toLowerCase()?.includes(query)
    );
    setFilteredContacts(filtered);
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setSearchQuery('');
  };

  const handleVoiceCall = () => {
    // Placeholder for voice call integration
    alert('Voice call feature - integrate with Daily.co, Twilio, or Agora');
  };

  const handleVideoCall = () => {
    // Placeholder for video call integration
    alert('Video call feature - integrate with Daily.co, Twilio, or Agora');
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  return (
    <>
      <Helmet>
        <title>Communication Hub - Form Forge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1
              className="text-3xl font-extrabold tracking-tight"
              style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif", letterSpacing: '-0.02em' }}
            >
              Communication Hub
            </h1>
            <p className="mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {isTrainer ? 'Connect with your clients in real-time' : 'Stay connected with your trainer'}
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#00D4FF' }}></div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6 h-[calc(100vh-250px)]">
              {/* Contact List Panel */}
              <ContactList
                contacts={filteredContacts}
                selectedContact={selectedContact}
                onContactSelect={handleContactSelect}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isTrainer={isTrainer}
                messages={messages}
                currentUserId={user?.id}
              />

              {/* Conversation Area */}
              <div
                className="col-span-12 md:col-span-8 rounded-xl flex flex-col"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
              >
                {selectedContact ? (
                  <>
                    {/* Conversation Header */}
                    <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                            style={{ background: 'rgba(0,212,255,0.12)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.25)', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                          >
                            {selectedContact?.fullName?.charAt(0)}
                          </div>
                          <div
                            className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2"
                            style={{ background: '#AAFF00', borderColor: '#0A0A0B', boxShadow: '0 0 6px #AAFF0099' }}
                          />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{selectedContact?.fullName}</p>
                          <p className="text-xs" style={{ color: isTyping ? '#AAFF00' : 'rgba(255,255,255,0.4)' }}>
                            {isTyping ? 'typing...' : 'Online'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleVoiceCall}
                          className="flex items-center justify-center w-9 h-9 rounded-lg transition-all"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
                        >
                          <Icon name="Phone" size={16} />
                        </button>
                        <button
                          onClick={handleVideoCall}
                          className="flex items-center justify-center w-9 h-9 rounded-lg transition-all"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
                        >
                          <Icon name="Video" size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Messages Area */}
                    <ConversationArea
                      messages={messages}
                      currentUserId={user?.id}
                      messagesEndRef={messagesEndRef}
                      formatMessageTime={formatMessageTime}
                    />

                    {/* Message Input */}
                    <MessageInput
                      newMessage={newMessage}
                      onMessageChange={setNewMessage}
                      onSendMessage={handleSendMessage}
                      isSending={isSending}
                    />
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    <div className="text-center">
                      <Icon name="MessageSquare" size={64} className="mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Select a contact to start messaging</p>
                      <p className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>Choose from your contacts on the left</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default CommunicationHub;